import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { renderToStaticMarkup } from 'react-dom/server'
import styled from 'styled-components'
import BarChart from 'components/visualization/Bar'
import messageUtil from 'utils/messageUtil'
import { CountType, InsightViewOption, DayHourTimeZone } from 'enums'
import { useDisplayTheme } from 'hooks/useUiState'
import { useResizeObserver } from 'hooks/useResizeObserver'
import { debounce } from 'lodash'
import { rounding } from 'utils/widgetUtil'

import { DayAndHourDataType, DayAndHourWidgetsType, InsightsSelectFunc } from '../../types'
import { getDisplayTimeZone, getWeekDayAbbreviation } from '../utils'
import Tooltip from './Tooltip'
import { getTooltipThemeByMode, getDataLabelColorByMode } from './themes'

const Container = styled.div`
  width: 100%;
  height: 100%;
`

interface BarDisplayProps {
  dayAndHourData: DayAndHourDataType
  widgetSetting: DayAndHourWidgetsType
  onInsightsSelect?: InsightsSelectFunc
}

const BarDisplay = ({ dayAndHourData, widgetSetting, onInsightsSelect }: BarDisplayProps) => {
  const theme = useDisplayTheme()
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const [tickInterval, setTickInterval] = useState(1)

  const { countType, viewOption, timeZoneOption } = widgetSetting
  const { dataSet, extraInfo } = dayAndHourData

  const xCategories = (dataSet ?? []).map((item) =>
    viewOption === InsightViewOption.HOUR_OF_DAY ? item.label : getWeekDayAbbreviation(item.label)
  )
  const barData = (dataSet ?? []).map((item) => item.share!)

  const adjustTickInterval = (observeEntry: ResizeObserverEntry[]) => {
    const containerWidth = observeEntry[0].contentRect.width
    if (viewOption === InsightViewOption.HOUR_OF_DAY) {
      // eslint-disable-next-line no-nested-ternary
      setTickInterval(containerWidth >= 900 ? 1 : containerWidth >= 300 ? 3 : 6)
    }
  }

  useResizeObserver(containerRef, debounce(adjustTickInterval, 10))

  return (
    <Container ref={containerRef}>
      <BarChart
        type="column"
        xCategories={xCategories}
        xAxisTitle={`${messageUtil.viewOption(viewOption!)} ${
          timeZoneOption === DayHourTimeZone.GMT
            ? getDisplayTimeZone(extraInfo!)
            : `(${t('authorsLocalTimeZone')})`
        }`}
        yAxisTitle={messageUtil.viewOption(InsightViewOption.PERCENT_SHARE)}
        data={[{ data: barData }]}
        tickInterval={tickInterval}
        showDataLabels
        showGridLineY
        showTickX
        showTickY={false}
        yAxisLabelFormatter={(val) => `${val}%`}
        dataLabelColor={getDataLabelColorByMode(theme)}
        colorSetting={
          countType === CountType.POSTS ? 'metric.mainMetrics.post' : 'metric.mainMetrics.authors'
        }
        hideOverlapDataLabels
        dataLabelFormatter={(point) => {
          if (point.y === 0) return null
          return rounding(point.y, 1)
        }}
        onClick={(point) => {
          const { x } = point
          const pointInfo = dataSet![x]
          onInsightsSelect?.([pointInfo])
        }}
        tooltipFormatter={({ x }) => {
          const pointInfo = dataSet![x]

          return renderToStaticMarkup(
            <Tooltip
              pointInfo={pointInfo}
              countType={countType!}
              tooltipTheme={getTooltipThemeByMode(theme)}
            />
          )
        }}
      />
    </Container>
  )
}

export default BarDisplay
